import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { FileText, ArrowUpRight } from "lucide-react";

const CATEGORY_ORDER = [
  "LEGAL & REGISTRATION",
  "GOVERNANCE",
  "ANNUAL REPORTS",
  "PROGRAMME REPORTS",
  "FINANCIAL INFORMATION",
  "POLICIES",
  "IMPACT REPORTS",
];

export default function Transparency() {
  const { data: reports = [] } = useQuery({
    queryKey: ["reports"],
    queryFn: async () => (await api.get("/reports")).data,
  });

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: reports.filter((r) => r.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      <Seo title="Transparency" path="/transparency" description="Legal, governance, financial and programme information for Planet Trotter Foundation." />
      <PageHero
        eyebrow="Transparency"
        titleLines={["Open by", "principle."]}
        intro="We are committed to transparency. As our documents, policies and reports are finalised, they will be published here in full."
        image={IMAGES.forest}
      />

      <section className="bg-ptf-bg py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="mb-14 max-w-2xl bg-ptf-accent/15 border border-ptf-accent/40 p-6">
              <p className="text-sm text-ptf-text/80 leading-relaxed">
                <strong className="text-ptf-primary">A note on honesty:</strong> We do
                not claim any registration, tax exemption or certification that has not
                yet been granted. Documents marked "To be published" are in progress or
                pending.
              </p>
            </div>
          </Reveal>

          <div className="space-y-14">
            {grouped.map((g, gi) => (
              <Reveal key={g.cat} delay={gi * 0.04}>
                <div>
                  <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight text-ptf-primary mb-6 pb-3 border-b border-ptf-primary/15">{g.cat}</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
                    {g.items.map((r) => {
                      const available = r.status !== "To be published" && r.url;
                      const El = available ? "a" : "div";
                      const props = available ? { href: r.url, target: "_blank", rel: "noopener noreferrer" } : {};
                      return (
                        <li key={r.id}>
                          <El {...props} className={`group flex items-center justify-between gap-4 py-4 border-b border-ptf-primary/10 ${available ? "cursor-pointer" : ""}`}>
                            <span className="flex items-center gap-3">
                              <FileText className={`h-4 w-4 shrink-0 ${available ? "text-ptf-secondary" : "text-ptf-text/30"}`} />
                              <span className={`${available ? "text-ptf-primary group-hover:text-ptf-secondary" : "text-ptf-text/75"} transition-colors`}>{r.title}</span>
                            </span>
                            {available ? (
                              <ArrowUpRight className="h-4 w-4 text-ptf-secondary" />
                            ) : (
                              <span className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/45 whitespace-nowrap">{r.status}</span>
                            )}
                          </El>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
